<template>
	<div class="lockOverrideRoot" role="dialog" aria-labelledby="lockOverrideTitle" aria-describedby="lockOverrideBody">
		<div class="titleBar">
			<div id="lockOverrideTitle" class="title" role="alert">Override the stale run lock</div>
		</div>
		<div id="lockOverrideBody" class="dialogBody">
			<div class="warningBox">
				Overriding the lock lets a new maintenance operation start even though the lock's owner never released it.  Do this
				<strong>only</strong> when you are certain the owning process is gone (crashed, stopped, or the machine is down).  If the
				owner is in fact still orchestrating, two orchestrators would be working on the same fleet.  The override is logged with
				your user name.
			</div>
			<div class="kvGrid">
				<span class="kvKey">Owner</span><span><strong>{{lock.ownerDisplayName || lock.actor || 'unknown'}}</strong></span>
				<span class="kvKey">Machine / PID</span><span><code class="inline">{{lock.ownerMachine || '?'}}</code> / {{lock.ownerPid || '?'}}<template v-if="lock.ownerVersion"> (version {{lock.ownerVersion}})</template></span>
				<span class="kvKey">Operation</span><span>{{lock.operation || '—'}}<template v-if="lock.runId"> — run <code class="inline">{{lock.runId}}</code></template></span>
				<span class="kvKey">Acquired</span><span>{{formatDateTime(lock.acquiredUtc)}}</span>
				<span class="kvKey">Last heartbeat</span><span>{{formatDateTime(lock.heartbeatUtc)}}<template v-if="heartbeatAge"> — {{heartbeatAge}} ago</template></span>
				<span class="kvKey">Other instance</span><span>{{lock.ownedByOtherInstance ? 'yes — the owner is a different TdsAppService process or machine' : 'no — the owner was this process'}}</span>
			</div>
			<label class="typeLabel" for="lockOwnerInput">Type the owner's display name exactly as shown above to enable the override:</label>
			<input id="lockOwnerInput" ref="ownerInput" type="text" class="ownerInput" v-model="typed" autocomplete="off" spellcheck="false"
				   name="do-not-autofill" data-bwignore="true" data-lpignore="true" :placeholder="lock.ownerDisplayName || ''" @keydown.esc.prevent="DefaultClose" />
			<div class="matchState" :class="matches ? 'matchOk' : 'matchNo'">{{matchText}}</div>
		</div>
		<div class="footer">
			<div class="slideWrap slideRed">
				<slide-button :key="'slide_lockOverride_' + (matches ? 1 : 0)"
							  :auto-width="false"
							  :circle="true"
							  :noanimate="true"
							  :width="320"
							  :height="50"
							  text="slide to override the lock"
							  success-text="overriding"
							  :disabled="!matches"
							  :requireReleaseToComplete="true"
							  @completed="onConfirm" />
			</div>
			<div ref="closeBtn" class="dialogButton cancelButton" role="button" tabindex="0" @click="DefaultClose" @keydown.space.enter.prevent="DefaultClose">Cancel</div>
		</div>
	</div>
</template>

<script>
	// Stale-lock override dialog.  Resolves { ownerName } when the administrator typed the owner's display name and completed the
	// slide control, false otherwise.  The typed name is sent as DbMaintenanceRunControlRequest.lockOwnerName so the server can
	// verify it against the lock file too.
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { formatDateTime, formatElapsed } from './runFormat';

	export default {
		components: { SlideButton },
		props:
		{
			/** DbMaintenanceLockInfo. */
			lock: {
				type: Object,
				required: true
			}
		},
		data()
		{
			return {
				typed: ''
			};
		},
		computed:
		{
			expected()
			{
				return this.lock.ownerDisplayName || '';
			},
			matches()
			{
				return !!this.expected && this.typed.trim() === this.expected;
			},
			matchText()
			{
				if (!this.expected)
					return 'The server did not report an owner display name; the override cannot be confirmed from this page.';
				if (!this.typed)
					return 'Waiting for the name…';
				return this.matches ? 'Name matches — the slide control is enabled.' : 'Does not match yet (the comparison is exact, including case and spacing).';
			},
			heartbeatAge()
			{
				if (!this.lock.heartbeatUtc)
					return null;
				let d = new Date(this.lock.heartbeatUtc);
				return isNaN(d.getTime()) ? null : formatElapsed(Date.now() - d.getTime());
			}
		},
		mounted()
		{
			this.$nextTick(() =>
			{
				if (this.$refs.ownerInput)
					this.$refs.ownerInput.focus();
			});
		},
		methods:
		{
			formatDateTime,
			SetFocus()
			{
				if (this.$refs.ownerInput)
					this.$refs.ownerInput.focus();
			},
			DefaultClose()
			{
				this.$emit('close', false);
			},
			onConfirm()
			{
				if (this.matches)
					this.$emit('close', { ownerName: this.typed.trim() });
			}
		}
	}
</script>

<style scoped>
	.lockOverrideRoot
	{
		background-color: #FFFFFF;
		min-width: 320px;
		max-width: min(720px, calc(100vw - 30px));
		max-height: calc(100vh - 30px);
		display: flex;
		flex-direction: column;
		word-break: break-word;
	}

	.titleBar
	{
		padding: 12px 16px 8px 16px;
		border-bottom: 1px solid #E0E0E0;
	}

	.title
	{
		text-align: center;
		font-weight: bold;
		font-size: 16pt;
		color: #8A0000;
	}

	.dialogBody
	{
		padding: 10px 16px 12px 16px;
		overflow-y: auto;
		flex: 1 1 auto;
		min-height: 0;
	}

	.footer
	{
		border-top: 1px solid #E0E0E0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: 10px 16px;
		padding: 10px 16px;
	}

	.dialogButton
	{
		display: inline-block;
		cursor: pointer;
		font-weight: bold;
		font-size: 12pt;
		padding: 10px 22px;
		border-radius: 5px;
		color: #CE7D29;
		border: 1px solid #DDDDDD;
		margin-left: auto;
	}

		.dialogButton:hover,
		.dialogButton:focus
		{
			background-color: rgba(0,0,0,0.05);
		}

	.slideWrap :deep(.slideunlock)
	{
		margin: 0;
	}

	.slideRed :deep(.slideunlock)
	{
		--su-color-bg: #F5DCDC;
		--su-color-progress-normal-bg: #E5AFAF;
		--su-color-progress-complete-bg: #CC3333;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin-bottom: 10px;
		line-height: 1.5;
	}

	.kvGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 4px 14px;
		align-items: baseline;
		margin: 6px 0 12px 0;
	}

	.kvKey
	{
		font-weight: bold;
		color: #444444;
		white-space: nowrap;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

	.typeLabel
	{
		display: block;
		font-weight: bold;
		margin-bottom: 4px;
	}

	.ownerInput
	{
		width: 100%;
		box-sizing: border-box;
		padding: 6px 10px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
		font-family: Consolas, monospace;
	}

	.matchState
	{
		font-size: 0.9em;
		margin-top: 4px;
	}

		.matchState.matchOk
		{
			color: #0A5A0A;
			font-weight: bold;
		}

		.matchState.matchNo
		{
			color: #7A5200;
		}
</style>
